import type SubstrateEvent from '../data/schema/SubstrateEvent';
import type SubstrateSearchScenario from '../data/schema/SubstrateSearchScenario';
import getSubstrateSearchEndpoint, {
    GENWEB_SEARCH_ENDPOINT_PREFIX,
} from '../helpers/getSubstrateSearchEndpoint';
import { logUsage } from 'owa-analytics';
import { getSessionId } from 'owa-config';
import { makePostRequest } from 'owa-ows-gateway';
import buildQueryParams from '../helpers/buildQueryParams';
import getUrlWithAddedQueryParameters from 'owa-url/lib/getUrlWithAddedQueryParameters';
import type { MailboxInfo } from 'owa-client-types';
import type SubstrateSearchEndpointTarget from '../data/schema/SubstrateSearchEndpointTarget';
import { isCapabilityEnabled } from 'owa-capabilities';
import { serviceRequestCapability } from 'owa-capabilities-definitions/lib/serviceRequestCapability';

interface EventsBatch {
    [traceId: string]: SubstrateEvent[];
}

interface EventBatchGroup {
    [batchKey: string]: EventsBatch;
}

interface EventRoutingContext {
    endpoint: string;
    mailboxInfo: MailboxInfo;
}

const maxRoutingContexts = 10000;
export const batch: Map<MailboxInfo, EventBatchGroup> = new Map<MailboxInfo, EventBatchGroup>();
const endpointByMailbox: Map<MailboxInfo, Map<SubstrateSearchScenario, string>> = new Map();
const routingContextByCorrelationId: Map<string, EventRoutingContext> = new Map();

export function registerSearchInstrumentationContext(
    mailboxInfo: MailboxInfo,
    substrateSearchScenario: SubstrateSearchScenario,
    endpointTarget: Exclude<SubstrateSearchEndpointTarget, 'Auto'>,
    correlationIds: Array<string | null | undefined>,
    endpointScenario: SubstrateSearchScenario = substrateSearchScenario
): void {
    const endpoint = getSubstrateSearchEndpoint(
        'events',
        undefined /* version */,
        endpointScenario,
        mailboxInfo,
        endpointTarget
    );
    const mailboxEndpoints = endpointByMailbox.get(mailboxInfo) ?? new Map();
    mailboxEndpoints.set(substrateSearchScenario, endpoint);
    endpointByMailbox.set(mailboxInfo, mailboxEndpoints);

    for (const correlationId of correlationIds) {
        if (correlationId) {
            routingContextByCorrelationId.delete(correlationId);
            routingContextByCorrelationId.set(correlationId, { endpoint, mailboxInfo });
        }
    }

    while (routingContextByCorrelationId.size > maxRoutingContexts) {
        const oldestKey = routingContextByCorrelationId.keys().next().value;
        if (oldestKey) {
            routingContextByCorrelationId.delete(oldestKey);
        } else {
            break;
        }
    }
}

export default function substrateSearchLogEventsOperation(
    traceId: string | null,
    event: SubstrateEvent,
    substrateSearchScenario: SubstrateSearchScenario,
    mailboxInfo: MailboxInfo
): void {
    const routingContext = traceId ? routingContextByCorrelationId.get(traceId) : undefined;
    const routedMailboxInfo = routingContext?.mailboxInfo ?? mailboxInfo;

    // Do not log if the mailbox does not support network requests, e.g. PST file or Unified Inbox
    if (!isCapabilityEnabled(serviceRequestCapability, routedMailboxInfo)) {
        return;
    }

    const nonNullAttributes = event.Attributes.filter(attribute => {
        return attribute.Value != null;
    });

    event.Attributes = nonNullAttributes;
    event.Attributes.forEach(element => {
        if (!!element.Value && typeof element.Value !== 'string') {
            throw new Error(
                'All values in substrate search event attributes must be strings or null/undefines'
            );
        }
    });
    const endpoint =
        routingContext?.endpoint ??
        endpointByMailbox.get(routedMailboxInfo)?.get(substrateSearchScenario) ??
        getSubstrateSearchEndpoint(
            'events',
            undefined /* version */,
            substrateSearchScenario,
            routedMailboxInfo
        );
    const batchKey = getBatchKey(substrateSearchScenario, endpoint);
    const mailboxBatch = batch.get(routedMailboxInfo) ?? {};
    if (mailboxBatch[batchKey] === undefined) {
        // if we don't yet have a batch, create one, and
        // wait 5sec before logging the batch
        mailboxBatch[batchKey] = <EventsBatch>{};
        setTimeout(logBatch(substrateSearchScenario, routedMailboxInfo, endpoint), 5000);
    }

    // Ensure we have an initialized batch, and add our event
    // to the right traceId key
    if (traceId != null) {
        mailboxBatch[batchKey][traceId] = (mailboxBatch[batchKey][traceId] || []).concat(event);
    }
    batch.set(routedMailboxInfo, mailboxBatch);
}

export function logBatch(
    substrateSearchScenario: SubstrateSearchScenario,
    mailboxInfo: MailboxInfo,
    endpoint: string = getSubstrateSearchEndpoint(
        'events',
        undefined /* version */,
        substrateSearchScenario,
        mailboxInfo
    )
) {
    return (): void => {
        if (!isCapabilityEnabled(serviceRequestCapability, mailboxInfo)) {
            return;
        }
        const mailboxBatch = batch.get(mailboxInfo) ?? {};
        const batchKey = getBatchKey(substrateSearchScenario, endpoint);
        const batchToLog = mailboxBatch[batchKey];
        if (!batchToLog) {
            return;
        }

        // transform the EventsBatch into the expected server schema
        // FROM: [{'traceId1': [eventA, eventB]}, {'traceId2': [eventC]}
        // TO: [{ Key: 'traceId1', Value: [eventA, eventB]}, {Key: 'traceId2': Value: [eventC]}]
        const body = Object.keys(batchToLog).reduce(
            (eventGroups: { Key: string; Value: SubstrateEvent[] }[], traceId) => {
                eventGroups.push({ Key: traceId, Value: batchToLog[traceId] });
                return eventGroups;
            },
            [] as { Key: string; Value: SubstrateEvent[] }[]
        );

        const defaultParams = { scenario: substrateSearchScenario };
        const params = buildQueryParams(defaultParams);

        delete mailboxBatch[batchKey];
        batch.set(mailboxInfo, mailboxBatch);

        const countsPerEvent: { [eventName: string]: number } = {};
        const totalEvents = Object.values(batchToLog).reduce((count, events) => {
            for (const event of events) {
                countsPerEvent[event.Name] = (countsPerEvent[event.Name] || 0) + 1;
            }
            return count + events.length;
        }, 0);
        const backend = endpoint.startsWith(GENWEB_SEARCH_ENDPOINT_PREFIX) ? 'GenWeb' : '3S';
        const logDelivery = (status: number): void => {
            const instPayload: { [key: string]: any } = {
                scenario: substrateSearchScenario,
                backend,
                status,
                totalEvents,
            };

            for (const key of Object.keys(countsPerEvent)) {
                instPayload[key] = countsPerEvent[key];
            }
            logUsage('SubstrateSearchInstrumentationEvent', mailboxInfo, instPayload);
        };

        makePostRequest(
            getUrlWithAddedQueryParameters(endpoint, params),
            body,
            undefined /* correlationId */,
            true /* returnFullResponse */,
            { 'Client-Session-Id': getSessionId() } /* customHeaders */,
            undefined /* throwServiceError */,
            undefined /* sendPayloadAsBody */,
            /* Search endpoints use the mailbox-scoped authentication supplied by the OWS gateway. */
            false /* includeCredentials */,
            'getSubstrateSearchEndpoint' /* actionName */,
            undefined /* RequestDatapointOptions */,
            mailboxInfo
        ).then(
            (response: any) => logDelivery(response.status),
            () => logDelivery(-1)
        );
    };
}

function getBatchKey(substrateSearchScenario: SubstrateSearchScenario, endpoint: string): string {
    return `${substrateSearchScenario}|${endpoint}`;
}

export function setBatchForTest(mailboxInfo: MailboxInfo, testBatch: EventBatchGroup): void {
    const convertedBatch: EventBatchGroup = {};
    for (const scenario of Object.keys(testBatch)) {
        const endpoint = getSubstrateSearchEndpoint(
            'events',
            undefined /* version */,
            scenario as SubstrateSearchScenario,
            mailboxInfo
        );
        convertedBatch[getBatchKey(scenario as SubstrateSearchScenario, endpoint)] =
            testBatch[scenario];
    }
    batch.set(mailboxInfo, convertedBatch);
}

export function getBatchForTest(
    substrateSearchScenario: SubstrateSearchScenario,
    mailboxInfo: MailboxInfo
): EventsBatch | undefined {
    const endpoint = getSubstrateSearchEndpoint(
        'events',
        undefined /* version */,
        substrateSearchScenario,
        mailboxInfo
    );
    return batch.get(mailboxInfo)?.[getBatchKey(substrateSearchScenario, endpoint)];
}

export function clearSearchInstrumentationContextForTest(): void {
    batch.clear();
    endpointByMailbox.clear();
    routingContextByCorrelationId.clear();
}
